import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { View, StyleSheet, ScrollView, Platform, Linking } from 'react-native'
import { Text, Button, CheckBox, Radio, RadioGroup, Spinner, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react-native'
import { UserContext } from '../../global/UserProvider'
import { aiAssistantsApi, httpStatusOf } from '../../global/AiAssistants/aiAssistantsApi'
import { apiTokensErrorCode } from '../../global/ApiTokens/apiTokensApi'
import {
  clientTitle,
  consentBlocker,
  consentScopeKey,
  decisionErrorKey,
  initialExpiryDays,
  initialScopes,
  loadFailureBlocker,
} from '../../global/AiAssistants/aiAssistantsView'
import {
  clearPendingConnectAiRequest,
  readPendingConnectAiRequest,
  rememberConnectAiRequest,
  resolveConnectAiRequestId,
} from '../../global/AiAssistants/connectAiRequest'

const STALE_CODES = ['request_not_pending', 'request_expired']

/**
 * ATL-1694 — the consent page Claude / ChatGPT send an admin to (`/connect-ai?request=<id>`) when
 * they connect to the Atlas MCP server. It shows who is asking — the HOST the access goes to, which
 * is the identity the person is trusting, ahead of the client's self-asserted name — what it asks
 * for, and for how long; "Allow" issues an OAuth connection with the ticked scopes and sends the
 * browser back to the assistant, "Deny" sends it back with `access_denied`.
 *
 * Every rule (admin only, the organization's MCP availability, the scopes that may be issued, the
 * active-token cap) is the backend's, re-checked on approve; this screen only shows what it says.
 * The request id survives a sign-in and a reload through `connectAiRequest.ts`.
 */
export default function ConnectAiScreen({ navigation, route }) {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const [context] = useContext(UserContext)
  const [requestId] = useState(() => resolveConnectAiRequestId(route?.params?.request, readPendingConnectAiRequest()))
  const [request, setRequest] = useState(null)
  const [blocker, setBlocker] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [loading, setLoading] = useState(requestId !== null)
  const [scopes, setScopes] = useState([])
  const [expiresInDays, setExpiresInDays] = useState(null)
  const [deciding, setDeciding] = useState(false)
  const [decisionError, setDecisionError] = useState(null)
  const [redirecting, setRedirecting] = useState(false)
  // A non-admin is told to sign in as an administrator; signing out unmounts this screen, and the
  // request must survive that so the container can bring the admin back to it after sign-in.
  const keepPendingOnLeaveRef = useRef(false)

  useEffect(() => {
    if (requestId) rememberConnectAiRequest(requestId)
    // Leaving the screen without deciding abandons the request: it must not reopen on the next
    // visit to Home. A web reload does not unmount, so the request survives one. The one exception
    // is a non-admin, who is sent to sign in as someone else (see keepPendingOnLeaveRef).
    return () => {
      if (!keepPendingOnLeaveRef.current) clearPendingConnectAiRequest()
    }
  }, [requestId])

  const load = useCallback(async () => {
    if (!requestId) return
    keepPendingOnLeaveRef.current = false
    setLoading(true)
    setLoadError(null)
    try {
      const loaded = await aiAssistantsApi.getRequest(requestId)
      const loadedBlocker = consentBlocker(loaded)
      setRequest(loaded)
      setBlocker(loadedBlocker)
      setScopes(initialScopes(loaded))
      setExpiresInDays(initialExpiryDays(loaded))
      if (loadedBlocker?.kind === 'stale') clearPendingConnectAiRequest()
    } catch (error) {
      const failure = loadFailureBlocker(httpStatusOf(error))
      if (failure) {
        setBlocker(failure)
        if (failure.kind === 'not_admin') keepPendingOnLeaveRef.current = true
        if (failure.kind !== 'not_admin') clearPendingConnectAiRequest()
      } else {
        Sentry.captureException(error, { tags: { feature: 'connect-ai' } })
        setLoadError(httpStatusOf(error) === null ? t('aiAssistants.networkError') : t('aiAssistants.genericError'))
      }
    } finally {
      setLoading(false)
    }
  }, [requestId, t])

  useEffect(() => {
    void load()
  }, [load])

  const toggleScope = (scope) => {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]))
  }

  const goTo = async (url) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
      window.location.assign(url)
      return
    }
    await Linking.openURL(url)
  }

  const decide = async (approve) => {
    if (!request) return
    if (approve && scopes.length === 0) {
      setDecisionError(t('aiAssistants.consentPickScope'))
      return
    }
    setDeciding(true)
    setDecisionError(null)
    try {
      const redirectTo = approve
        ? await aiAssistantsApi.approve(request.id, { scopes, expiresInDays })
        : await aiAssistantsApi.deny(request.id)
      clearPendingConnectAiRequest()
      setRedirecting(true)
      await goTo(redirectTo)
    } catch (error) {
      const code = apiTokensErrorCode(error)
      const status = httpStatusOf(error)
      if (code && STALE_CODES.includes(code)) clearPendingConnectAiRequest()
      if (status === null || (status >= 500 && !code)) Sentry.captureException(error, { tags: { feature: 'connect-ai' } })
      const key = decisionErrorKey(code, status)
      setDecisionError(i18n.exists(key) ? t(key) : t('aiAssistants.genericError'))
      setRedirecting(false)
    } finally {
      setDeciding(false)
    }
  }

  const card = [styles.card, { backgroundColor: theme['background-basic-color-1'] }]

  // Pressing «На главную» is an explicit abandonment, the non-admin's included: only the sign-out
  // the not-admin message asks for keeps the request.
  const goHome = () => {
    keepPendingOnLeaveRef.current = false
    navigation.navigate('Home')
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <Spinner />
      </View>
    )
  }

  const renderMessage = (text, status) => (
    <ScrollView style={styles.container} contentContainerStyle={styles.content} testID='connect-ai-screen'>
      <View style={card}>
        <Text category='s1' style={styles.gap}>{t('aiAssistants.consentTitle')}</Text>
        <Text status={status} testID='connect-ai-message'>{text}</Text>
        <View style={styles.row}>
          {loadError && (
            <Button size='small' onPress={load} style={styles.rowButton}>{t('aiAssistants.retry')}</Button>
          )}
          <Button size='small' status='basic' onPress={() => navigation.navigate('AiAssistants')} style={styles.rowButton}>
            {t('aiAssistants.openHelp')}
          </Button>
          <Button size='small' appearance='ghost' onPress={goHome} style={styles.rowButton}>
            {t('aiAssistants.goHome')}
          </Button>
        </View>
      </View>
    </ScrollView>
  )

  if (!requestId) return renderMessage(t('aiAssistants.noRequest'), 'basic')
  if (loadError) return renderMessage(loadError, 'danger')
  if (blocker?.kind === 'not_admin') return renderMessage(t('aiAssistants.blocked_not_admin'), 'warning')
  if (blocker?.kind === 'not_found') return renderMessage(t('aiAssistants.blocked_not_found'), 'warning')
  if (blocker?.kind === 'stale') return renderMessage(t('aiAssistants.blocked_stale'), 'warning')
  if (!request) return renderMessage(t('aiAssistants.genericError'), 'danger')
  if (blocker?.kind === 'unavailable') return renderMessage(t(blocker.key), 'warning')

  const who = clientTitle(request.client)
  const clientLabel = t(who.key)

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content} testID='connect-ai-screen'>
      <View style={card}>
        <Text category='h6' style={styles.gap}>{t('aiAssistants.consentTitle')}</Text>
        <Text style={styles.gap}>
          {t('aiAssistants.consentAsking', { client: clientLabel, organization: request.organization.name })}
        </Text>
        <Text category='s1' style={[styles.host, { backgroundColor: theme['background-basic-color-2'] }]} testID='connect-ai-host'>
          {t('aiAssistants.consentHost', { host: who.host })}
        </Text>
        {request.client.name && (
          <Text category='c1' appearance='hint' style={styles.topGap}>
            {t('aiAssistants.consentClientName', { name: request.client.name })}
          </Text>
        )}
        {request.client.kind === 'local' && (
          <Text status='warning' style={styles.topGap} testID='connect-ai-local-warning'>
            {t('aiAssistants.consentLocalWarning')}
          </Text>
        )}
        {request.client.kind === 'other' && (
          <Text status='warning' style={styles.topGap} testID='connect-ai-other-warning'>
            {t('aiAssistants.consentOtherWarning')}
          </Text>
        )}
      </View>

      <View style={card}>
        <Text category='s1' style={styles.gap}>{t('aiAssistants.consentScopesTitle')}</Text>
        {request.requestedScopes.map((scope) => {
          const key = consentScopeKey(scope)
          return (
            <CheckBox
              key={scope}
              checked={scopes.includes(scope)}
              onChange={() => toggleScope(scope)}
              disabled={deciding || redirecting}
              style={styles.gap}
              testID={`connect-ai-scope-${scope}`}
            >
              {i18n.exists(key) ? t(key) : scope}
            </CheckBox>
          )
        })}
        {request.requestedScopes.some((s) => s.startsWith('draft:')) && (
          <Text appearance='hint' category='c1'>{t('aiAssistants.consentDraftNote')}</Text>
        )}
      </View>

      {request.allowedExpiresInDays.length > 0 && (
        <View style={card}>
          <Text category='s1' style={styles.gap}>{t('aiAssistants.consentExpiresTitle')}</Text>
          <RadioGroup
            selectedIndex={request.allowedExpiresInDays.indexOf(expiresInDays)}
            onChange={(index) => setExpiresInDays(request.allowedExpiresInDays[index])}
          >
            {request.allowedExpiresInDays.map((days) => (
              <Radio key={days} disabled={deciding || redirecting}>{t('apiTokens.days', { count: days })}</Radio>
            ))}
          </RadioGroup>
        </View>
      )}

      <View style={card}>
        <Text appearance='hint' category='c1' style={styles.gap}>
          {t('aiAssistants.consentActor', { name: context?.user?.name ?? '—' })}
        </Text>
        {decisionError && <Text status='danger' style={styles.gap} testID='connect-ai-error'>{decisionError}</Text>}
        {redirecting ? (
          <View style={styles.row}>
            <Spinner size='small' />
            <Text style={styles.redirecting}>{t('aiAssistants.redirecting', { client: clientLabel })}</Text>
          </View>
        ) : (
          <View style={styles.row}>
            <Button
              onPress={() => decide(true)}
              disabled={deciding || scopes.length === 0 || expiresInDays === null}
              style={styles.rowButton}
              testID='connect-ai-allow'
            >
              {t('aiAssistants.allow')}
            </Button>
            <Button
              status='basic'
              onPress={() => decide(false)}
              disabled={deciding}
              style={styles.rowButton}
              testID='connect-ai-deny'
            >
              {t('aiAssistants.deny')}
            </Button>
          </View>
        )}
      </View>
      <View style={styles.bottomSpacer} />
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  bottomSpacer: { height: 50 },
  container: { flex: 1, padding: 20 },
  // Room below the last line for the app-wide floating help button, which otherwise covers it.
  content: { paddingBottom: 96 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  card: { borderRadius: 10, padding: 15, marginTop: 10 },
  gap: { marginBottom: 8 },
  topGap: { marginTop: 8 },
  row: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginTop: 12 },
  rowButton: { marginRight: 10, marginBottom: 6 },
  host: { padding: 10, borderRadius: 6 },
  redirecting: { marginLeft: 10 },
})
